/**
 * Axios 配置
 * 提供简洁的 HTTP 客户端配置
 */

import axios, {
    AxiosHeaders,
    type AxiosInstance,
    type AxiosRequestConfig,
    type AxiosResponse,
    type AxiosError
} from 'axios'
import {Message} from '@arco-design/web-vue'
import {getTokenManager} from './token'
import i18n from '@/i18n'
import {getLoginRoute} from '@utils/app-config'
import router from '@/router'
import {useAuthStore} from '@/stores/auth'
import {MessageCode} from '@/enums/message-code'

/**
 * 错误提示去重管理器
 */
class ErrorMessageManager {
    private messageCache = new Map<string, number>()
    private readonly DEBOUNCE_TIME = 3000 // 3秒内相同错误只提示一次

    /**
     * 检查是否应该显示错误提示
     * @param key 错误标识key
     * @returns 是否应该显示
     */
    shouldShow(key: string): boolean {
        const now = Date.now()
        const lastShowTime = this.messageCache.get(key)

        if (!lastShowTime || now - lastShowTime > this.DEBOUNCE_TIME) {
            this.messageCache.set(key, now)
            return true
        }

        return false
    }

    /**
     * 清理过期的缓存
     */
    private cleanup() {
        const now = Date.now()
        for (const [key, time] of this.messageCache.entries()) {
            if (now - time > this.DEBOUNCE_TIME) {
                this.messageCache.delete(key)
            }
        }
    }

    constructor() {
        // 定期清理过期缓存
        setInterval(() => this.cleanup(), this.DEBOUNCE_TIME)
    }
}

const errorMessageManager = new ErrorMessageManager()
let isHandlingUnauthorized = false

type AuthTrackedRequestConfig = AxiosRequestConfig & {
    __authToken?: string
    __skipAuth?: boolean
}

type ResponsePreservingRequestConfig = AxiosRequestConfig & {
    preserveResponse?: boolean
}

const TRANSPORT_CONSIGNMENT_EXTERNAL_PATH_PREFIXES = [
    '/external/consignment/',
    '/supplier/space-booking/'
]

/** 仅识别运输委托的两个供应商外链，避免改变其他匿名页面和系统页面的 401 行为。 */
function isTransportConsignmentExternalPage(): boolean {
    const currentPath = window.location.pathname
    return TRANSPORT_CONSIGNMENT_EXTERNAL_PATH_PREFIXES.some(path =>
        currentPath.startsWith(path) || currentPath.startsWith(`/client${path}`)
    )
}

/** 请求头 token：优先 Cookie，Pinia 持久化有值时回写 Cookie（避免只刷新页面后接口无 Authorization） */
function resolveAuthToken(): string | undefined {
    const tokenManager = getTokenManager()
    const fromCookie = tokenManager.get()
    if (fromCookie) {
        return fromCookie
    }
    if (isTransportConsignmentExternalPage()) {
        // 运输委托外链只能使用匿名 Cookie，禁止回退并复制员工会话 token。
        return undefined
    }
    const fromStore = useAuthStore().token
    if (fromStore) {
        tokenManager.set(fromStore, 7)
        return fromStore
    }
    return undefined
}

function readCurrentAuthToken(): string | undefined {
    return getTokenManager().get() || useAuthStore().token || undefined
}

const AUTH_FREE_PATHS = [
    '/enterprise/tenant/user/login',
    '/enterprise/tenant/user/login/code',
    '/enterprise/tenant/user/login/verify',
    '/enterprise/tenant/user/login/token',
    '/enterprise/tenant/user/login/sso',
    '/enterprise/tenant/user/login/sso/verify',
    '/enterprise/tenant/user/register',
    '/enterprise/tenant/user/register/code',
    '/enterprise/tenant/user/password/reset',
    '/enterprise/tenant/user/password/reset/code',
    '/enterprise/client/user/login',
    '/enterprise/client/user/login/code',
    '/enterprise/client/user/login/verify',
    '/enterprise/client/user/register',
    '/enterprise/client/user/register/code',
    '/enterprise/client/user/password/reset',
    '/enterprise/client/user/password/reset/code',
    // 登录页按域名读取租户配置（公开接口），不携带、不校验残留 token
    '/enterprise/enterprise-config/by-domain',
    // 登录页按域名下载租户品牌文件（系统图标等，公开接口）
    '/enterprise/enterprise-config/download-file-by-domain',
    // 匿名 token 签发必须忽略残留 token，否则过期匿名 token 会在刷新前被网关拒绝。
    '/enterprise/auth/anonymous-access/buildToken'
]

function getRequestPath(url?: string): string {
    if (!url) {
        return ''
    }

    try {
        return new URL(url, window.location.origin).pathname
    } catch {
        return url.split('?')[0]
    }
}

function shouldSkipAuthHeader(config: AxiosRequestConfig): boolean {
    const requestPath = getRequestPath(config.url)
    return AUTH_FREE_PATHS.some(path => requestPath.endsWith(path))
}

function removeAuthorizationHeader(config: AxiosRequestConfig) {
    if (!config.headers) {
        return
    }

    if (config.headers instanceof AxiosHeaders) {
        config.headers.delete('Authorization')
        return
    }

    delete (config.headers as Record<string, unknown>).Authorization
    delete (config.headers as Record<string, unknown>).authorization
}

function handleUnauthorizedError(error: AxiosError, showErrorMessage = true) {
    const authConfig = error.config as AuthTrackedRequestConfig | undefined
    const requestToken = authConfig?.__authToken
    const currentToken = readCurrentAuthToken()

    if (authConfig?.__skipAuth) {
        return
    }

    if (isTransportConsignmentExternalPage()) {
        // 外链鉴权失败只清匿名 Cookie，员工会话和系统页面的登录状态保持不变。
        getTokenManager().remove()
        return
    }

    if (currentToken && requestToken !== currentToken) {
        console.warn('[CT_API_AUTH] 忽略旧请求返回的 401，当前已存在新的登录状态')
        return
    }

    if (showErrorMessage && errorMessageManager.shouldShow('http_401')) {
        Message.warning(i18n.global.t('common.httpError.unauthorized'))
    }

    if (isHandlingUnauthorized) {
        return
    }
    isHandlingUnauthorized = true

    getTokenManager().remove()
    useAuthStore().resetState()

    const loginRoute = getLoginRoute()
    if (router.currentRoute.value.path !== loginRoute) {
        router.push(loginRoute).finally(() => {
            isHandlingUnauthorized = false
        })
    } else {
        isHandlingUnauthorized = false
    }
}

/**
 * 递归将对象中的所有 undefined 转换为 null
 * 防止 Axios 自动移除 undefined 字段
 *
 * @param obj 需要处理的对象
 * @returns 处理后的对象
 */
function convertUndefinedToNull(obj: any): any {
    if (obj === null || obj === undefined) {
        return ''
    }

    // 处理日期对象
    if (obj instanceof Date) {
        return obj
    }

    // 处理 FormData（文件上传）
    if (obj instanceof FormData) {
        return obj
    }

    // 处理数组
    if (Array.isArray(obj)) {
        return obj.map(item => convertUndefinedToNull(item))
    }

    // 处理普通对象
    if (typeof obj === 'object') {
        const result: any = {}
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const value = obj[key]
                result[key] = value === undefined ? '' : convertUndefinedToNull(value)
            }
        }
        return result
    }

    // 其他基础类型直接返回
    return obj
}

/**
 * 创建 Axios 实例
 */
function createHttpClient(): AxiosInstance {
    const client = axios.create({
        baseURL: '/gateway',
        timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 10000,
        headers: {
            'Content-Type': 'application/json'
        }
    })

    // 请求拦截器
    client.interceptors.request.use(
        config => {
            const skipAuth = shouldSkipAuthHeader(config)
            const token = skipAuth ? undefined : resolveAuthToken()
            if (token) {
                config.headers.Authorization = token
            } else if (skipAuth) {
                removeAuthorizationHeader(config)
            }
            const authConfig = config as AuthTrackedRequestConfig
            authConfig.__authToken = token
            authConfig.__skipAuth = skipAuth

            // 根据上下文国际化添加国际化请求头
            const lang = i18n.global.locale.value === 'zh-CN' ? 'zh_CN' : 'en_US'
            const acceptLang = i18n.global.locale.value === 'zh-CN' ? 'zh-CN,zh;q=0.9' : 'en-US,en;q=0.9'
            config.headers.language = lang
            config.headers['Accept-Language'] = acceptLang

            // multipart 需由浏览器自动带 boundary，不能沿用默认 application/json
            if (config.data instanceof FormData && config.headers) {
                if (config.headers instanceof AxiosHeaders) {
                    config.headers.delete('Content-Type')
                } else {
                    delete (config.headers as Record<string, unknown>)['Content-Type']
                }
            }

            // 统一处理 undefined 转换为 null（包括 data 和 params）
            if (config.data) {
                config.data = convertUndefinedToNull(config.data)
            }
            if (config.params) {
                config.params = convertUndefinedToNull(config.params)
            }

            console.log(`[CT_API] ${config.method?.toUpperCase()} ${config.url}`)
            return config
        },
        error => {
            console.error('[CT_API_ERROR] 请求错误:', error.message)
            return Promise.reject(error)
        }
    )

    // 响应拦截器
    client.interceptors.response.use(
        (response: AxiosResponse<ApiResponse>) => {
            const {data, status} = response

            // 如果是字节流响应（如文件下载、图片等），直接返回
            if (
                response.config.responseType === 'blob' ||
                response.config.responseType === 'arraybuffer'
            ) {
                if ((response.config as ResponsePreservingRequestConfig).preserveResponse) {
                    return response
                }
                return data
            }

            // 如果是文本响应
            if (response.config.responseType === 'text') {
                return data
            }

            // 只处理 HTTP 200
            if (status !== 200) {
                return Promise.reject(new Error(`HTTP ${status}`))
            }

            // HTTP 200 成功
            if (data && typeof data === 'object' && 'ok' in data && 'code' in data && 'message' in data) {
                // 业务成功：ok = true
                if (data.ok === true) {
                    // 如果有 token，自动保存
                    if (data.token) {
                        getTokenManager().set(data.token)
                        console.log('[CT_API_TOKEN] Token 已更新')
                    }

                    // 返回 content 字段，如果没有 content 则返回整个响应
                    return 'content' in data ? data.content : data
                } else {
                    // 业务失败：ok = false
                    const errorMessage = data.message || '业务处理失败'
                    console.warn(`[CT_API_BUSINESS_ERROR] ${errorMessage}`)

                    // 使用 ArcoDesign 的 Message 显示错误提示（带去重）
                    // 增加 showErrorMessage 配置支持，默认显示
                    const showErrorMessage = (response.config as any).showErrorMessage !== false
                    if (showErrorMessage && MessageCode.NOT_PRIVILEGE_VIEW !== data.code
                        && errorMessageManager.shouldShow(`business_error_${data.code}_${errorMessage}`)) {
                        if (data.code === 'PRICE_EXACT_DUPLICATE') {
                            Message.error(i18n.global.t('error.price.duplicate'))
                        } else {
                            Message.error(errorMessage)
                        }
                    }

                    const error = new Error(errorMessage)
                    ;(error as any).code = data.code
                    ;(error as any).content = data.content
                    return Promise.reject(error)
                }
            }

            // 非标准格式或其他格式，直接返回数据
            return data
        },
        (error: AxiosError) => {
            // 页面刷新、关闭或卸载主动取消请求时，不产生用户错误提示。
            if (axios.isCancel(error)) {
                return Promise.reject(error)
            }
            const status = error.response?.status
            const message = (error.response?.data as any)?.message || error.message
            const showErrorMessage = (error.config as any)?.showErrorMessage !== false

            // 处理常见 HTTP 错误（带去重）
            if (status === 401) {
                handleUnauthorizedError(error, showErrorMessage)
            } else if (status === 403) {
                console.warn('[CT_API_PERMISSION] 权限不足')
                if (showErrorMessage && errorMessageManager.shouldShow('http_403')) {
                    Message.error(i18n.global.t('common.httpError.forbidden'))
                }
            } else if (status === 404) {
                const requestUrl = String(error.config?.url || '')
                const u = requestUrl.replace(/^\//, '')
                const silent404ByPath =
                    u.includes('mdm/universal-search')
                    || u.includes('tenant/mdm/')
                    || u.includes('user-config/tenant/get')
                    || u.includes('universal/business/reminder')
                    || u.includes('universal/aiConversation')
                    || u.includes('api/v1/ehr')
                console.warn('[CT_API_NOT_FOUND] 请求的资源不存在', requestUrl)
                if (
                    showErrorMessage
                    && !silent404ByPath
                    && errorMessageManager.shouldShow(`http_404_${requestUrl}`)
                ) {
                    Message.error(i18n.global.t('error.common.http404'))
                }
            } else if (status && status >= 500) {
                console.error('[CT_API_SERVER] 服务器错误')
                if (showErrorMessage && errorMessageManager.shouldShow(`http_${status}`)) {
                    Message.error(i18n.global.t('common.httpError.serverError'))
                }
            } else {
                console.error('[CT_API_ERROR] 请求失败:', message)
                if (showErrorMessage && errorMessageManager.shouldShow(`http_error_${message}`)) {
                    Message.error(message || i18n.global.t('common.httpError.requestFailed'))
                }
            }

            return Promise.reject(error)
        }
    )

    return client
}

// 创建默认实例
export const httpClient = createHttpClient()

// 导出 Token 管理器（从 token.config.ts 重新导出）
export {getTokenManager, TokenManager} from './token'

export const request = {
    get: <T = any>(url: string, params?: any, config?: AxiosRequestConfig) =>
        httpClient.get<any, T>(url, {params, ...config}),

    post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        httpClient.post<any, T>(url, data, config),

    put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        httpClient.put<any, T>(url, data, config),

    patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
        httpClient.patch<any, T>(url, data, config),

    delete: <T = any>(url: string, params?: any, config?: AxiosRequestConfig) =>
        httpClient.delete<any, T>(url, {params, ...config})
}

// 文件上传客户端
export const uploadClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
    timeout: 60000, // 上传超时时间较长
    headers: {
        'Content-Type': 'multipart/form-data'
    }
})

// 上传客户端请求拦截器
uploadClient.interceptors.request.use(config => {
    const token = getTokenManager().get()
    if (token) {
        config.headers.Authorization = token
    }
    const authConfig = config as AuthTrackedRequestConfig
    authConfig.__authToken = token
    authConfig.__skipAuth = false

    // 统一处理 undefined 转换为 null（包括 data 和 params）
    // 注意：FormData 会被正确识别并跳过转换
    if (config.data) {
        config.data = convertUndefinedToNull(config.data)
    }
    if (config.params) {
        config.params = convertUndefinedToNull(config.params)
    }

    return config
})

uploadClient.interceptors.response.use(
    response => response,
    (error: AxiosError) => {
        if (error.response?.status === 401) {
            handleUnauthorizedError(error)
        }
        return Promise.reject(error)
    }
)
